<script setup>
import MarketNoteMark from 'components/market/MarketNoteMark.vue';
import marketConfig from 'src/configs/market';
import { formatMarketCount, formatMarketMetric } from 'src/utils/market-util';
import { trackProductEvent } from 'src/utils/umami-util';
import { buildXprtJobsCategoryUrl } from 'src/utils/xprt-jobs-route-util';
import { computed, ref } from 'vue';

const headerClasses = 'font-mono text-uppercase';

const columns = [
    { name: 'rank', label: '#', field: 'jobCount', align: 'right', classes: 'gt-xs', headerClasses: `gt-xs ${headerClasses}` },
    { name: 'name', label: 'Specialisation', field: 'name', align: 'left', sortable: true, headerClasses },
    { name: 'jobCount', label: 'Active roles', field: 'jobCount', align: 'right', sortable: true, headerClasses },
    {
        name: 'median',
        label: 'Median pay',
        field: row => row.salaries?.median ?? null,
        align: 'right',
        sortable: true,
        headerClasses
    },
    {
        name: 'share',
        label: 'Share',
        field: 'jobCount',
        align: 'left',
        classes: 'gt-xs',
        headerClasses: `gt-xs ${headerClasses}`
    }
];

const $props = defineProps({
    isAvailable: {
        type: Boolean,
        default: false
    },
    limit: {
        type: Number,
        default: null
    },
    locationSlug: {
        type: String,
        default: marketConfig.countrySlug
    },
    rows: {
        type: Array,
        default: () => []
    },
    title: {
        type: String,
        default: 'Active ICT specialisations'
    }
});

const pagination = ref({ rowsPerPage: 0, sortBy: 'jobCount', descending: true });

const orderedRows = computed(() => {
    const rows = [...$props.rows].sort((first, second) => second.jobCount - first.jobCount);
    return $props.limit ? rows.slice(0, $props.limit) : rows;
});

const leadingCount = computed(() => orderedRows.value[0]?.jobCount || 0);

const countLabel = computed(() => {
    if ($props.limit && $props.rows.length > orderedRows.value.length) {
        return `Top ${orderedRows.value.length} of ${$props.rows.length}`;
    }
    return formatMarketCount(orderedRows.value.length, 'specialisation');
});

// A withheld median has no value to order by, so it stays at the foot of the table whichever way the column is sorted
// rather than jumping to the top when the order is reversed.
function sortRows(rows, sortBy, descending) {
    const column = columns.find(candidate => candidate.name === sortBy);
    if (!column) {
        return rows;
    }
    return [...rows].sort((first, second) => {
        const firstValue = readField(column, first);
        const secondValue = readField(column, second);
        if (firstValue === null || secondValue === null) {
            return (firstValue === null) - (secondValue === null);
        }
        const order = typeof firstValue === 'string' ? firstValue.localeCompare(secondValue) : firstValue - secondValue;
        return descending ? -order : order;
    });
}

function readField(column, row) {
    const value = typeof column.field === 'function' ? column.field(row) : row[column.field];
    return value ?? null;
}

function describeSort(column) {
    if (!column.sortable) {
        return undefined;
    }
    if (pagination.value.sortBy !== column.name) {
        return 'none';
    }
    return pagination.value.descending ? 'descending' : 'ascending';
}

function formatMedian(row) {
    return formatMarketMetric(row.salaries.median, { type: 'salary', currency: row.salaries.currency || 'AUD' });
}

function trackSpecialisationSelection(classificationSlug) {
    trackProductEvent('specialisation_selected', {
        component: 'market_specialisation_table',
        classificationSlug
    });
}

function trackJobsHandoff(classificationSlug) {
    trackProductEvent('xprt_jobs_handoff', {
        component: 'market_specialisation_table',
        classificationSlug,
        locationSlug: $props.locationSlug
    });
}
</script>

<template>
    <section aria-labelledby="market-specialisations-heading" class="market-specialisation-table">
        <div class="row items-end justify-between q-gutter-sm q-mb-sm">
            <div>
                <h2 id="market-specialisations-heading" class="text-h4 text-weight-bold q-mt-none q-mb-xs">
                    {{ $props.title }}<MarketNoteMark :number="1"/>
                </h2>
                <p class="text-body2 text-hint q-my-none">
                    Active roles by specialisation, ordered by demand. Bars compare each specialisation with the leader.
                    Median pay is the advertised median<MarketNoteMark :number="2"/>, withheld where too few listings
                    state pay.
                </p>
            </div>
            <span v-if="orderedRows.length" class="font-mono text-caption text-hint">{{ countLabel }}</span>
        </div>
        <q-table
            v-if="orderedRows.length"
            v-model:pagination="pagination"
            :columns="columns"
            :rows="orderedRows"
            :sort-method="sortRows"
            class="market-table"
            flat
            hide-pagination
            row-key="id"
            separator="horizontal"
            wrap-cells
        >
            <template #header-cell="scope">
                <q-th :aria-sort="describeSort(scope.col)" :props="scope">{{ scope.col.label }}</q-th>
            </template>
            <template #body-cell-rank="scope">
                <q-td :props="scope" class="text-hint" data-test="market-rank">{{ String(scope.rowIndex + 1).padStart(2, '0') }}</q-td>
            </template>
            <template #body-cell-name="scope">
                <q-td :props="scope">
                    <a
                        :href="`/market/${scope.row.slug}`"
                        class="table-link text-body1 text-weight-bold"
                        data-test="market-detail-link"
                        @click="trackSpecialisationSelection(scope.row.slug)"
                    >
                        {{ scope.row.name }}
                    </a>
                    <div>
                        <a
                            :href="buildXprtJobsCategoryUrl(scope.row.slug, $props.locationSlug)"
                            class="table-link text-caption"
                            data-test="jobs-link"
                            rel="noopener noreferrer"
                            target="_blank"
                            @click="trackJobsHandoff(scope.row.slug)"
                        >
                            View jobs on Xprt Jobs
                            <q-icon aria-hidden="true" class="q-ml-xs" name="sym_r_north_east" size="14px"/>
                        </a>
                    </div>
                </q-td>
            </template>
            <template #body-cell-jobCount="scope">
                <q-td :props="scope" class="font-mono text-weight-medium">{{ formatMarketMetric(scope.value) }}</q-td>
            </template>
            <template #body-cell-median="scope">
                <q-td :props="scope" class="font-mono" data-test="market-median">
                    <span v-if="scope.value !== null" class="text-weight-medium">{{ formatMedian(scope.row) }}</span>
                    <span v-else class="text-hint" title="Too few listings state pay to show a median">Withheld</span>
                </q-td>
            </template>
            <template #body-cell-share="scope">
                <q-td :props="scope">
                    <q-linear-progress :value="leadingCount ? scope.value / leadingCount : 0" class="market-meter" color="primary" size="8px"/>
                </q-td>
            </template>
        </q-table>
        <q-banner v-else class="empty-state text-hint" role="status">
            <template #avatar>
                <q-icon :name="$props.isAvailable ? 'sym_r_check_circle' : 'sym_r_monitoring'" color="primary"/>
            </template>
            <template v-if="$props.isAvailable">
                <strong>No active ICT specialisations were observed in this snapshot.</strong>
                <div class="text-body2 q-mt-xs">The successful measurement returned no active child classifications.</div>
            </template>
            <template v-else>
                <strong>Specialisation data is currently unavailable.</strong>
                <div class="text-body2 q-mt-xs">Check back after the next market data refresh.</div>
            </template>
        </q-banner>
    </section>
</template>

<style lang="scss" scoped>
.market-specialisation-table {
    .market-table {
        background: transparent;

        :deep(.q-table td:nth-child(5)) {
            width: 24%;
        }

        :deep(.q-table td:first-child) {
            width: 1%;
        }

        // Three columns at Quasar's 16px cell padding are wider than a 390px phone, so a phone halves it.
        @media (max-width: $breakpoint-xs-max) {
            :deep(.q-table th),
            :deep(.q-table td) {
                padding-left: 8px;
                padding-right: 8px;
            }
        }
    }

    // Both links in a row sit one above the other, so each holds at least the 24px a touch target needs.
    .table-link {
        align-items: center;
        display: inline-flex;
        min-height: 24px;
    }

    .empty-state {
        background: rgba($primary, 0.06);
        border: 1px solid rgba($primary, 0.16);
    }
}
</style>
